import { addMonths, type LocalDate } from '../dates';
import type { DomainEvent } from '../events';
import type { Mutation } from '../mutations';
import { isValidLocalDate } from '../budget/schedule';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import { expenseDateFor, isOpen, payeeDues } from './project';
import {
  duePaymentId,
  paymentEntryId,
  paymentsMutation,
  type Due,
  type DueRowStatus,
  type PayeeLike,
  type PaymentLike,
  type PaymentsResult,
  type PaymentsSnapshot,
} from './types';

export type PayError = 'bad_amount' | 'bad_date' | 'not_open' | 'not_found';

export interface PayInput {
  amountMinor: number;
  paidDate: LocalDate;
  /**
   * The amount turned out different (auto-pay "Different amount", a utility's real bill): the
   * due's amount becomes what has been paid in all, so it closes as paid.
   */
  setAmount?: boolean;
  /**
   * The auto-pay "Did it auto-pay?" Confirm: the payment and its ledger row get the due's
   * deterministic ids, so two devices confirming the same auto-debit write one row. Every typed
   * payment gets fresh random ids instead (two partial payments never merge, and undo-then-pay
   * never lands on a live payment's id).
   */
  confirm?: boolean;
}

const validAmount = (n: number) => Number.isInteger(n) && n > 0 && n <= MAX_AMOUNT_MINOR;

/**
 * The text on the ledger row: the lender for loans (spec), "BDO share to Papa" for a
 * supplementary card, else the payee's name.
 */
export function paymentNote(
  payee: Pick<PayeeLike, 'kind' | 'name' | 'lender' | 'cardholder'>,
): string {
  if (payee.kind === 'loan' && payee.lender) return payee.lender;
  if (payee.kind === 'card' && payee.cardholder)
    return `${payee.name} share to ${payee.cardholder}`;
  return payee.name;
}

/**
 * Write fields of a due row: a bare insert-if-absent creates it (payee and period only), then an
 * update writes just the changed fields, so a skip on one device and a typed amount on another
 * both survive the merge.
 */
function dueRowWrite(
  due: Pick<Due, 'id' | 'payeeId' | 'period' | 'stored'>,
  patch: { amountMinor?: number | null; minimumMinor?: number | null; status?: DueRowStatus },
  ctx: BuildCtx,
): Mutation[] {
  const update = paymentsMutation(ctx, 'payment_dues', 'update', due.id, patch);
  if (due.stored) return [update];
  return [
    paymentsMutation(
      ctx,
      'payment_dues',
      'insert',
      due.id,
      { payeeId: due.payeeId, period: due.period },
      { ifAbsent: true },
    ),
    update,
  ];
}

export interface PayResult {
  mutations: Mutation[];
  event: DomainEvent;
  payment: PaymentLike;
}

/**
 * Pay a due, in full or in part (OPEN-QUESTIONS v0.5 Q2: a partial payment keeps the rest due).
 * A typed payment gets fresh ids; only the auto-pay Confirm uses the due's deterministic ids (see
 * PayInput.confirm). A pay-any-time loan's due with no stored amount stores the amount it showed
 * (the planned payment, or less when the balance is smaller), so it stays paid as the balance
 * changes. The 'payment.paid' event lets the payments.paid-expense
 * rule write the expense (or, for a card statement, the transfer) when Spending is usable.
 */
export function buildPayDue(
  userId: string,
  due: Due,
  payee: PayeeLike,
  input: PayInput,
  opts: { spending: boolean; accountId?: string | null },
  ctx: BuildCtx,
): PaymentsResult<PayResult, PayError> {
  if (!isOpen(due) && !(due.status === 'rolled' && due.transfer))
    return { ok: false, error: 'not_open' };
  if (!validAmount(input.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(input.paidDate)) return { ok: false, error: 'bad_date' };
  const n = due.payments.length + 1;
  const paymentId = input.confirm ? duePaymentId(userId, payee.id, due.period, n) : ctx.newId();
  const ledgerEntryId = !opts.spending
    ? null
    : input.confirm
      ? paymentEntryId(userId, payee.id, due.period, n)
      : ctx.newId();
  const payment: PaymentLike = {
    id: paymentId,
    payeeId: payee.id,
    dueId: due.id,
    period: due.period,
    paidDate: input.paidDate,
    amountMinor: input.amountMinor,
    ledgerEntryId,
    note: null,
    deletedAt: null,
  };
  const insert = paymentsMutation(ctx, 'payments', 'insert', paymentId, {
    payeeId: payee.id,
    dueId: due.id,
    period: due.period,
    paidDate: input.paidDate,
    amountMinor: input.amountMinor,
    currency: 'PHP',
    ledgerEntryId,
    note: null,
  });
  const mutations: Mutation[] = [insert];
  if (input.setAmount) {
    mutations.push(...dueRowWrite(due, { amountMinor: due.paidMinor + input.amountMinor }, ctx));
  } else if (
    payee.kind === 'loan' &&
    payee.loanType === 'flexible' &&
    (due.stored?.amountMinor ?? null) === null
  ) {
    mutations.push(...dueRowWrite(due, { amountMinor: due.amountMinor }, ctx));
  }
  return {
    ok: true,
    mutations,
    payment,
    event: paidEvent(
      payee,
      payment,
      expenseDateFor(input.paidDate, due.dueDate),
      due.transfer,
      ctx,
      insert.id,
      opts.accountId,
    ),
  };
}

/** A flexible loan's payment at any time (no due): lowers the balance, writes a Loan payment expense. */
export function buildExtraPayment(
  payee: PayeeLike,
  input: { amountMinor: number; paidDate: LocalDate },
  opts: { spending: boolean; accountId?: string | null },
  ctx: BuildCtx,
): PaymentsResult<PayResult, PayError> {
  if (!validAmount(input.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(input.paidDate)) return { ok: false, error: 'bad_date' };
  const paymentId = ctx.newId();
  const ledgerEntryId = opts.spending ? ctx.newId() : null;
  const payment: PaymentLike = {
    id: paymentId,
    payeeId: payee.id,
    dueId: null,
    period: null,
    paidDate: input.paidDate,
    amountMinor: input.amountMinor,
    ledgerEntryId,
    note: null,
    deletedAt: null,
  };
  const insert = paymentsMutation(ctx, 'payments', 'insert', paymentId, {
    payeeId: payee.id,
    dueId: null,
    period: null,
    paidDate: input.paidDate,
    amountMinor: input.amountMinor,
    currency: 'PHP',
    ledgerEntryId,
    note: null,
  });
  return {
    ok: true,
    mutations: [insert],
    payment,
    event: paidEvent(
      payee,
      payment,
      input.paidDate,
      payee.kind === 'card',
      ctx,
      insert.id,
      opts.accountId,
    ),
  };
}

/**
 * The planned due a "Pay any amount" payment on a pay-any-time loan goes toward: the earliest open
 * one due up to a month after the payment (an overdue one first), or null.
 */
export function dueForAnyAmount(dues: readonly Due[], paidDate: LocalDate): Due | null {
  const limit = addMonths(paidDate, 1);
  return dues.find((d) => isOpen(d) && d.remainingMinor > 0 && d.dueDate <= limit) ?? null;
}

/**
 * "Pay any amount" on a loan: paid toward the nearest open planned due when there is one (so the
 * due clears and Budget counts the money once), else a payment of its own (buildExtraPayment).
 */
export function buildPayAnyAmount(
  userId: string,
  snap: PaymentsSnapshot,
  payee: PayeeLike,
  input: { amountMinor: number; paidDate: LocalDate },
  opts: { spending: boolean; accountId?: string | null },
  ctx: BuildCtx,
  today: LocalDate,
): PaymentsResult<PayResult, PayError> {
  if (isValidLocalDate(input.paidDate) && payee.kind === 'loan') {
    const limit = addMonths(input.paidDate, 1);
    const due = dueForAnyAmount(
      payeeDues(snap, payee, limit > today ? limit : today, today),
      input.paidDate,
    );
    if (due) return buildPayDue(userId, due, payee, input, opts, ctx);
  }
  return buildExtraPayment(payee, input, opts, ctx);
}

function paidEvent(
  payee: PayeeLike,
  payment: PaymentLike,
  date: LocalDate,
  transfer: boolean,
  ctx: BuildCtx,
  mutationId: string,
  accountId?: string | null,
): DomainEvent {
  return {
    type: 'payment.paid',
    paymentId: payment.id,
    payeeId: payee.id,
    payeeKind: payee.kind,
    ledgerEntryId: payment.ledgerEntryId,
    transfer,
    amountMinor: payment.amountMinor,
    date,
    categoryId: payee.categoryId,
    fundId: payee.fundId,
    ...(accountId !== undefined ? { accountId } : {}),
    note: paymentNote(payee),
    at: ctx.now,
    mutationId,
  };
}

/** Undo a payment: the payment and its ledger row are soft-deleted (History keeps them). */
export function buildUndoPayment(
  payment: Pick<PaymentLike, 'id' | 'ledgerEntryId'>,
  ctx: BuildCtx,
): Mutation[] {
  return [
    paymentsMutation(ctx, 'payments', 'delete', payment.id),
    ...(payment.ledgerEntryId
      ? [paymentsMutation(ctx, 'ledger_entries', 'delete', payment.ledgerEntryId)]
      : []),
  ];
}

/** Skip this one (a bill not charged this month); undone by buildSetDueStatus(..., 'open'). */
export function buildSetDueStatus(due: Due, status: DueRowStatus, ctx: BuildCtx): Mutation[] {
  if ((due.stored?.status ?? 'open') === status) return [];
  return dueRowWrite(due, { status }, ctx);
}

/** The real amount: a utility's bill, a card's real statement, a bill that changed this time. */
export function buildSetDueAmount(
  due: Due,
  amountMinor: number | null,
  ctx: BuildCtx,
): PaymentsResult<{ mutations: Mutation[] }, PayError> {
  if (
    amountMinor !== null &&
    !(Number.isInteger(amountMinor) && amountMinor >= 0 && amountMinor <= MAX_AMOUNT_MINOR)
  )
    return { ok: false, error: 'bad_amount' };
  return { ok: true, mutations: dueRowWrite(due, { amountMinor }, ctx) };
}

/** A card statement's real minimum payment. */
export function buildSetDueMinimum(
  due: Due,
  minimumMinor: number | null,
  ctx: BuildCtx,
): PaymentsResult<{ mutations: Mutation[] }, PayError> {
  if (minimumMinor !== null && !validAmount(minimumMinor))
    return { ok: false, error: 'bad_amount' };
  return { ok: true, mutations: dueRowWrite(due, { minimumMinor }, ctx) };
}
